import { type ReactNode, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import { AssignProjectManagerModal } from "../../Projects/ProjectsLeadApproval/AssignProjectManagerModal.tsx"
import {
  type ProjectManagerRequired,
  readProjectManagerRequired
} from "../helpers/project-manager-required.ts"

interface UseProjectManagerPromptArgs<Context> {
  /** Resends the refused change with the chosen PM; rejects when it fails. */
  submit: (context: Context, projectManagerId: string) => Promise<unknown>
  /** The user backed out — the change was never made. */
  onCancel?: (context: Context) => void
  /** Pre-selected when offered, as the manual "Create From Lead" flow does. */
  defaultProjectManagerId?: string
}

/**
 * The "choose the PM first" answer a Won gets when Settings > Configurations'
 * Automatic Project Creation is on: holds the refused change, asks for the PM
 * from the server's list, and resends it. Another refusal of the same kind
 * keeps the question open; any other failure closes it, since the caller has
 * already reported that failure.
 */
export const useProjectManagerPrompt = <Context,>({
  submit,
  onCancel,
  defaultProjectManagerId
}: UseProjectManagerPromptArgs<Context>): {
  ask: (required: ProjectManagerRequired, context: Context) => void
  dismiss: () => void
  dialog: ReactNode
} => {
  const { t } = useTranslation()
  const [prompt, setPrompt] = useState<
    (ProjectManagerRequired & { context: Context }) | null
  >(null)
  const [submitting, setSubmitting] = useState(false)

  const confirm = async (projectManagerId: string) => {
    if (!prompt || submitting) return
    setSubmitting(true)
    try {
      await submit(prompt.context, projectManagerId)
      setPrompt(null)
    } catch (rejection) {
      const again = readProjectManagerRequired(rejection)
      setPrompt(again ? { ...again, context: prompt.context } : null)
    } finally {
      setSubmitting(false)
    }
  }

  const dismiss = useCallback(() => setPrompt(null), [])

  const cancel = () => {
    if (!prompt || submitting) return
    onCancel?.(prompt.context)
    setPrompt(null)
  }

  const offered = prompt?.projectManagers ?? []
  const defaultSelectedId = offered.some(
    (option) => option.value === defaultProjectManagerId
  )
    ? defaultProjectManagerId
    : undefined

  return {
    ask: (required, context) => setPrompt({ ...required, context }),
    dismiss,
    dialog: prompt ? (
      <AssignProjectManagerModal
        projectManagers={offered}
        defaultSelectedId={defaultSelectedId}
        loading={submitting}
        description={t("leadProjectManagerPrompt.description")}
        onConfirm={(projectManagerId) => void confirm(projectManagerId)}
        onCancel={cancel}
      />
    ) : null
  }
}
